import { ApolloClient, ApolloLink, Operation } from "@apollo/client"
import { ErrorResponse } from "@apollo/client/link/error"
import {
  Box,
  Dialog,
  HStack,
  IconButton,
  useDisclosure
} from "@chakra-ui/react"
import { toaster } from "@pathwright/ux/src/components/chakra/ui/toaster"
import Link from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { print } from "graphql"
import LZString from "lz-string"
import { useEffect, useMemo, useState, useSyncExternalStore } from "react"
import {
  getEnv,
  getNodeEnvValue,
  getOperatingEnv,
  getTargetEnv
} from "../env/env"
import { isSuperUserSessionTransfer } from "../token/utils"

declare const process: {
  env: {
    REACT_APP_TEST_RUNNER?: string
  }
}

export type GraphQLPath = "/graphql" | "/blocks/graphql"

// A simpler representation of an operation, uses less storage.
export type SimpleApolloOperation = Pick<
  Operation,
  "operationName" | "variables"
> & {
  query: string
  hasError?: boolean
  graphqlPath?: GraphQLPath
}

export function extractSimpleApolloOperation(
  operation: Operation,
  graphqlPath: GraphQLPath = "/graphql"
): SimpleApolloOperation {
  return {
    operationName: operation.operationName,
    variables: operation.variables,
    query: print(operation.query),
    hasError: false,
    graphqlPath
  }
}

export function operationMatchesSimpleApolloOperation(
  operation: Operation,
  simpleOperation: SimpleApolloOperation,
  graphqlPath: GraphQLPath = simpleOperation.graphqlPath || "/graphql"
): boolean {
  const providedSimpleOperation = extractSimpleApolloOperation(
    operation,
    graphqlPath
  )

  return (
    providedSimpleOperation.operationName === simpleOperation.operationName &&
    providedSimpleOperation.query === simpleOperation.query &&
    providedSimpleOperation.variables === simpleOperation.variables &&
    (simpleOperation.graphqlPath || "/graphql") === graphqlPath
  )
}

// Gets a link to the Apollo Sandbox for the given operation and target environment.
export function getApolloSandboxLink(
  operation: SimpleApolloOperation,
  targetLocalHost: boolean
) {
  const graphqlPath = operation.graphqlPath || "/graphql"

  const options: {
    document?: string
    variables?: string
    operationName?: string
    headers?: string
  } = {
    document: operation.query,
    variables: JSON.stringify(operation.variables),
    operationName: operation.operationName
  }

  // Grab the school (space) ID from localStorage (should always be available).
  const spaceId = localStorage.getItem("PW_SPACE")

  const headers = new Headers()

  // Include the headers option if an auth_token is found in the current search params.
  if (localStorage.getItem("auth_token")) {
    headers.set("Authorization", "Token " + localStorage.getItem("auth_token"))
  }

  // Include Debug header for local devs. This enables more verbose SQL logging on the server side.
  if (targetLocalHost) {
    headers.set("Debug", "true")
  }

  // Stringify headers for Apollo Sandbox.
  options.headers = JSON.stringify(Object.fromEntries(headers.entries()))

  // Apollo uses LZString compression for their explorerURLState param.
  // See GitHub discussion: https://github.com/apollographql/apollo-studio-community/discussions/127?utm_source=chatgpt.com#discussioncomment-2150944
  const explorerURLState = LZString.compressToEncodedURIComponent(
    JSON.stringify(options)
  )

  // Query params to pass to the sandbox.
  const queryParams = `school_id=${spaceId}&explorerURLState=${explorerURLState}`

  if (targetLocalHost) {
    return `http://localhost:8000${graphqlPath}?${queryParams}`
  } else {
    const endpoint = getTargetEnv(getOperatingEnv(), {
      production: "https://gql.pathwright.com",
      staging: "https://gql.pathwrightstaging.com",
      testing: "https://gql.pantswright.com",
      development: "https://gql.pathwrightdev.com"
    })

    return `https://studio.apollographql.com/sandbox/explorer?endpoint=${endpoint}${graphqlPath}?${queryParams}`
  }
}

// Equivalent to getIsPWAdminUtilEnabled, without web-client dependencies.
function getIsPWAdminUtilEnabled() {
  if (typeof localStorage === "undefined") return false
  if (isSuperUserSessionTransfer()) return true
  try {
    const options = localStorage.getItem("admin::")
    return options ? JSON.parse(options).length > 0 : false
  } catch {
    return false
  }
}

const MAX_OPERATIONS = 500

let _operations: SimpleApolloOperation[] = []
const _operationsListeners = new Set<() => void>()

function emitOperationsChange() {
  _operationsListeners.forEach((listener) => listener())
}

function subscribeToOperations(listener: () => void) {
  _operationsListeners.add(listener)
  return () => {
    _operationsListeners.delete(listener)
  }
}

function getOperationsSnapshot() {
  return _operations
}

export function recordApolloOperation(
  operation: Operation,
  { graphqlPath }: { graphqlPath: GraphQLPath }
) {
  if (!getIsPWAdminUtilEnabled()) return

  // Defer state updates to avoid updating React during another component's
  // render phase (e.g., when Apollo kicks off an operation synchronously
  // during useQuery render in a child component).
  setTimeout(() => {
    _operations = [
      extractSimpleApolloOperation(operation, graphqlPath),
      ..._operations
    ].slice(0, MAX_OPERATIONS)
    emitOperationsChange()
  }, 0)
}

export function markApolloOperationError(
  operation: Operation,
  { graphqlPath }: { graphqlPath: GraphQLPath }
) {
  if (!getIsPWAdminUtilEnabled()) return

  setTimeout(() => {
    let changed = false
    _operations = _operations.map((op) => {
      if (
        operationMatchesSimpleApolloOperation(operation, op, graphqlPath) &&
        !op.hasError
      ) {
        changed = true
        return { ...op, hasError: true }
      }
      return op
    })
    if (changed) emitOperationsChange()
  }, 0)
}

export function useApolloOperations() {
  return useSyncExternalStore(
    subscribeToOperations,
    getOperationsSnapshot,
    () => []
  )
}

// --- Client registry (keyed by graphqlPath) ---

const _clientsByPath = new Map<GraphQLPath, ApolloClient<any>>()

export function registerApolloClient(
  graphqlPath: GraphQLPath,
  client: ApolloClient<any>
) {
  _clientsByPath.set(graphqlPath, client)
}

export function getRegisteredApolloClient(
  graphqlPath: GraphQLPath = "/graphql"
): ApolloClient<any> | undefined {
  return _clientsByPath.get(graphqlPath)
}

// --- Error toast / modal ---

type SetSelectedError = (error: ErrorResponse | null) => void

let _setSelectedError: SetSelectedError | null = null

function registerSelectedErrorSetter(setter: SetSelectedError) {
  _setSelectedError = setter
  return () => {
    if (_setSelectedError === setter) {
      _setSelectedError = null
    }
  }
}

export function notifyApolloOperationError(
  error: ErrorResponse,
  { graphqlPath }: { graphqlPath: GraphQLPath }
) {
  const { operation, graphQLErrors } = error
  // Diagnostics are for developers, including deployed staging/testing sites.
  // Automated clients still receive the form's user-facing error feedback.
  if (
    graphQLErrors &&
    !getEnv("production") &&
    getNodeEnvValue() !== "test" &&
    process.env.REACT_APP_TEST_RUNNER !== "true"
  ) {
    const operationName = operation.operationName || "Unknown Operation"

    const simpleOperation = extractSimpleApolloOperation(operation, graphqlPath)
    const developmentLink = getApolloSandboxLink(simpleOperation, true)
    const operatingEnvLink = getApolloSandboxLink(simpleOperation, false)

    // NOTE: The links are not rendering as links and thus are not clickable.
    // This may be due to the Chakra v3 upgrade, where it's possible the description
    // may only work as a string (though it does render JSX).
    toaster.create({
      title: "GraphQL Error",
      description: (
        <HStack w="100%">
          <Link
            color="white"
            colorPalette="whiteAlpha"
            href={developmentLink}
            variant="underline"
          >
            {operationName}
          </Link>
          <IconButton
            color="white"
            colorPalette="whiteAlpha"
            variant="ghost"
            minH="2em"
            minW="2em"
            ml="auto"
            borderRadius="full"
            aria-label="View Error Details"
            onClick={(e) => {
              e.stopPropagation()
              _setSelectedError?.(error)
            }}
            children={<Pathicon icon="caution-circle" />}
          />
          <Link asChild href={operatingEnvLink}>
            <IconButton
              color="white"
              colorPalette="whiteAlpha"
              variant="ghost"
              minH="2em"
              minW="2em"
              ml="auto"
              borderRadius="full"
              aria-label="GraphQL Operation Link"
              onClick={(e) => e.stopPropagation()}
              children={<Pathicon icon="globe-fill" />}
            />
          </Link>
        </HStack>
      ),
      type: "error",
      duration: 5000,
      closable: true
    })
  }
}

export function handleApolloOperationError(
  error: ErrorResponse,
  { graphqlPath }: { graphqlPath: GraphQLPath }
) {
  notifyApolloOperationError(error, { graphqlPath })
  if (error.graphQLErrors) {
    markApolloOperationError(error.operation, { graphqlPath })
  }
}

export function createApolloOperationLink(graphqlPath: GraphQLPath) {
  return new ApolloLink((operation, forward) => {
    recordApolloOperation(operation, { graphqlPath })
    return forward(operation)
  })
}

// Hook that mounts the error-details modal and registers setSelectedError
// so toasts from any client can open it. Returns the modal element.
export function useApolloOperationErrorToast() {
  const [selectedError, setSelectedError] = useState<ErrorResponse | null>(null)

  useEffect(() => registerSelectedErrorSetter(setSelectedError), [])

  const disclosure = useDisclosure({
    open: !!selectedError,
    onClose: () => setSelectedError(null)
  })

  // Maybe we should render the cmdk menu with these error details shown?
  const modal = useMemo(
    () => (
      <Dialog.Root
        open={disclosure.open}
        onOpenChange={(e: { open: boolean }) => !e.open && disclosure.onClose()}
        size="full"
      >
        <Dialog.Backdrop />
        {/* @ts-ignore Chakra v3 compound component children typing under node resolution */}
        <Dialog.Positioner>
          {/* @ts-ignore Chakra v3 compound component children typing under node resolution */}
          <Dialog.Content bg="white">
            <Dialog.Header>
              <strong>{selectedError?.operation.operationName}</strong> Error
              Details
            </Dialog.Header>
            {/* @ts-ignore Chakra v3 compound component children typing under node resolution */}
            <Dialog.CloseTrigger asChild>
              <IconButton
                aria-label="Close error details"
                variant="ghost"
                size="sm"
                rounded="full"
              >
                <Pathicon icon="x" />
              </IconButton>
            </Dialog.CloseTrigger>
            <Dialog.Body>
              <Box
                maxW="100%"
                overflowX="auto"
                whiteSpace="pre-wrap"
                mb="4"
                bg="gray.800"
                color="gray.100"
                borderRadius="2xl"
                p={2}
              >
                <pre>
                  {JSON.stringify(selectedError?.graphQLErrors, null, 2)}
                </pre>
              </Box>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    ),
    [disclosure.open, disclosure.onClose, selectedError]
  )

  return modal
}
